import {
  ICreateEventBoolPropertyPayload,
  ICreateEventIntPropertyPayload,
  ICreateEventStringPropertyPayload,
  IEventBoolProperty,
  IEventIntProperty,
  IEventResponse,
  IEventStringProperty,
  IPaginatedEvents,
  IOrganizationEvent,
  ICreateOrganizationEventPayload,
  IConfirmOrganizationEventAttendancePayload,
  IPaginatedOrganizationEventAttendees,
  IFileUploadResponse,
  IOrganizationEventAttendanceStats,
} from "@/types/EventTypes";
import { IEventFormData, PaginatedProduct } from "@/types/ProductTypes";
import axios, { AxiosInstance } from "axios";

export class V1BEClient {
  private client: AxiosInstance;

  /**
   * @param baseURL base url of the api
   * @param version version of the api (e.g. v1)
   */
  constructor(baseURL: string, version: string) {
    this.client = axios.create({
      baseURL: baseURL + "/" + version,
    });
  }

  setToken(token: string) {
    this.client.defaults.headers.common["Authorization"] =
      "Bearer " + token.trim();
  }

  // Events
  createEvent = async (
    data: IEventFormData
  ): Promise<IEventResponse | null> => {
    try {
      return this.client
        .post("/events/", data)
        .then((res: any) => res.data)
        .catch((_) => {
          return null;
        });
    } catch (error: any) {
      return null;
    }
  };

  updateEvent = async (
    data: IEventFormData,
    eventId: number
  ): Promise<IEventResponse | null> => {
    try {
      const res = await this.client.patch(`/events/${eventId}`, data);

      return res.data;
    } catch (error: any) {
      return null;
    }
  };

  deleteEvent = async (eventId: number): Promise<boolean> => {
    try {
      await this.client.delete(`/events/${eventId}`);
      return true;
    } catch (error: any) {
      return false;
    }
  };

  fetchMyEvents = async (query: string = ""): Promise<IPaginatedEvents> => {
    try {
      const { data } = await this.client.get<IPaginatedEvents>(
        `/events/own?${query}`
      );
      return data;
    } catch (error: any) {
      return {
        data: [],
        total: 0,
        current_page: 0,
      };
    }
  };

  fetchMyProducts = async (query: string = ""): Promise<PaginatedProduct> => {
    try {
      const { data } = await this.client.get<PaginatedProduct>(
        `/products/my?${query}`
      );
      return data;
    } catch (error: any) {
      return {
        data: [],
        total: 0,
        current_page: 0,
      };
    }
  };

  fetchProducts = async (query: string = ""): Promise<PaginatedProduct> => {
    try {
      const { data } = await this.client.get<PaginatedProduct>(
        `/products/?${query}`
      );
      return data;
    } catch (error: any) {
      return {
        data: [],
        total: 0,
        current_page: 0,
      };
    }
  };

  createEventStringProperty = async (
    eventId: number,
    data: ICreateEventStringPropertyPayload[]
  ): Promise<IEventStringProperty[] | null> => {
    try {
      return this.client
        .post(`/events/${eventId}/string-properties`, data)
        .then((res: any) => res.data);
    } catch (err: any) {
      return null;
    }
  };

  createEventBoolProperty = async (
    eventId: number,
    data: ICreateEventBoolPropertyPayload[]
  ): Promise<IEventBoolProperty[] | null> => {
    try {
      return this.client
        .post(`/events/${eventId}/bool-properties`, data)
        .then((res: any) => res.data);
    } catch (err: any) {
      return null;
    }
  };

  deleteEventStringProperty = async (
    eventId: number,
    propertyIds: string
  ): Promise<number | null> => {
    try {
      return this.client
        .delete(`/events/${eventId}/string-properties?ids=${propertyIds}`)
        .then((res: any) => res.status);
    } catch (err: any) {
      return null;
    }
  };

  createOrganizationEventStringProperties = async (
    organizationEventId: string,
    data: ICreateEventStringPropertyPayload[]
  ): Promise<IEventStringProperty[] | null> => {
    try {
      return this.client
        .post(
          `/organization-events/${organizationEventId}/string-properties`,
          data
        )
        .then((res: any) => res.data);
    } catch (err: any) {
      return null;
    }
  };

  createOrganizationEventIntProperties = async (
    organizationEventId: string,
    data: ICreateEventIntPropertyPayload[]
  ): Promise<IEventIntProperty[] | null> => {
    try {
      return this.client
        .post(
          `/organization-events/${organizationEventId}/int-properties`,
          data
        )
        .then((res: any) => res.data);
    } catch (err: any) {
      return null;
    }
  };

  deleteEventBoolProperty = async (
    eventId: number,
    propertyIds: string
  ): Promise<number | null> => {
    try {
      return this.client
        .delete(`/events/${eventId}/bool-properties?ids=${propertyIds}`)
        .then((res: any) => res.status);
    } catch (err: any) {
      return null;
    }
  };

  // Organization Events
  createOrganizationEvent = async (
    data: ICreateOrganizationEventPayload
  ): Promise<IOrganizationEvent | null> => {
    try {
      return this.client
        .post("/organization-events/", data)
        .then((res: any) => res.data)
        .catch((_) => {
          return null;
        });
    } catch (error: any) {
      return null;
    }
  };

  updateOrganizationEvent = async (
    data: ICreateOrganizationEventPayload
  ): Promise<boolean> => {
    try {
      await this.client.patch("/organization-events/", data);
      return true;
    } catch (error: any) {
      return false;
    }
  };

  deleteOrganizationEvent = async (id: string): Promise<boolean> => {
    try {
      await this.client.delete(`/organization-events/${id}`);
      return true;
    } catch (error: any) {
      return false;
    }
  };

  getOrganizationEvents = async (params?: {
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: string;
    date_from?: string;
    date_to?: string;
    title?: string;
  }): Promise<IOrganizationEvent[]> => {
    try {
      const { data } = await this.client.get<IOrganizationEvent[]>(
        "/organization-events/",
        { params }
      );
      return data;
    } catch (error: any) {
      return [];
    }
  };

  getOrganizationEvent = async (
    id: string
  ): Promise<IOrganizationEvent | null> => {
    try {
      const { data } = await this.client.get<IOrganizationEvent>(
        `/organization-events/${id}`
      );
      return data;
    } catch (error: any) {
      return null;
    }
  };

  confirmOrganizationEventAttendance = async (
    data: IConfirmOrganizationEventAttendancePayload
  ): Promise<any> => {
    try {
      return this.client
        .post("/organization-events/attendance", data)
        .then((res: any) => res.data)
        .catch((_) => {
          return null;
        });
    } catch (error: any) {
      return null;
    }
  };

  getOrganizationEventAttendees = async (params?: {
    organization_event_id?: string;
    status?: string;
    attending_as?: string;
    user_id?: number;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<IPaginatedOrganizationEventAttendees | null> => {
    try {
      const { data } =
        await this.client.get<IPaginatedOrganizationEventAttendees>(
          "/organization-events/attendance",
          { params }
        );
      return data;
    } catch (error: any) {
      console.error("Failed to get organization event attendees:", error);
      return null;
    }
  };

  getOrganizationEventAttendanceStats = async (
    organizationId: string,
    params?: {
      organization_event_ids?: string;
      event_date_from?: string;
      event_date_to?: string;
    }
  ): Promise<IOrganizationEventAttendanceStats | null> => {
    try {
      const { data } = await this.client.get<IOrganizationEventAttendanceStats>(
        `/organization-events/attendance/stats/${organizationId}`,
        { params }
      );
      return data;
    } catch (error: any) {
      console.error(
        "Failed to get organization event attendance stats:",
        error
      );
      return null;
    }
  };

  uploadFile = async (file: File): Promise<IFileUploadResponse | null> => {
    try {
      // 1. Get Presigned URL
      const { data: presignedData } = await this.client.get(
        "/files/upload-url",
        {
          params: {
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
          },
        }
      );

      if (!presignedData || !presignedData.upload_url) {
        return null;
      }

      // 2. Upload directly to S3 (using raw axios to avoid default headers)
      await axios.put(presignedData.upload_url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      // 3. Save Metadata
      const metadataPayload = {
        key: presignedData.key,
        file_url: presignedData.public_url,
        file_type: file.type,
      };

      const { data: fileData } = await this.client.post(
        "/files",
        metadataPayload
      );

      return fileData;
    } catch (error) {
      console.error("File upload failed:", error);
      return null;
    }
  };

  deleteFile = async (fileUrl: string): Promise<boolean> => {
    try {
      await this.client.delete(`/files?file_url=${fileUrl}`);
      return true;
    } catch (error: any) {
      return false;
    }
  };
}
